/*
  Spoken Cycles - buy box savings label.

  Yuva renders its savings element as "15% OFF" (or "$59.85 OFF", depending on
  the theme's saved-amount setting) and rewrites it from common.js whenever the
  variant changes. The PDP design calls for "Save $59.85" instead, so rather
  than fork Yuva's price snippet this rewrites the element in place and
  re-applies on every variant change via a MutationObserver.

  Falls back silently: if anything here fails the native label stays visible.
*/
(function () {
  'use strict';

  var SELECTOR = '.yv-main-product-detail [data-price-wrapper]';

  function variantData(root) {
    var script = root.querySelector('script[data-name="variant-json"]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      return null;
    }
  }

  // Falls back to the variant the snippet published when Yuva's own variant
  // JSON is missing, which happens on a template with the add-to-cart block
  // disabled, since that JSON is rendered inside its form.
  function currentVariant(root, variants, cfg) {
    return selectedVariant(root, variants) || (cfg && cfg.variant) || null;
  }

  function selectedVariant(root, variants) {
    if (!variants || !variants.length) return null;
    var input = root.querySelector('input[name="id"]');
    var id = input && parseInt(input.value, 10);
    if (!id) return variants[0];
    for (var i = 0; i < variants.length; i++) {
      if (parseInt(variants[i].id, 10) === id) return variants[i];
    }
    return variants[0];
  }

  function money(cents) {
    // snippets/theme-scripts.liquid declares `const moneyFormat` at the top
    // level of a classic script, so it is a global lexical binding rather than
    // a property of window.
    var format =
      typeof moneyFormat !== 'undefined' && moneyFormat
        ? moneyFormat
        : '${{amount}}';

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, format);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function apply(priceBox, productRoot, variant) {
    var label = priceBox.querySelector('.yv-product-percent-off');
    if (!label || !variant) return;

    var compare = parseInt(variant.compare_at_price, 10);
    var price = parseInt(variant.price, 10);
    if (!compare || !price || compare <= price) return;

    var text = 'Save ' + money(compare - price);
    if (label.textContent.trim() !== text) {
      label.textContent = text;
    }
  }

  // ---- meta rows -----------------------------------------------------------
  // The stock line, delivery promise and collect row are rendered by
  // spoken-buybox-meta.liquid for whichever variant Liquid picked. Yuva swaps
  // the price and the button on a variant change but knows nothing about these
  // rows, so without this a sold-out variant kept reading "In stock".

  function config(root) {
    var el = root.querySelector('[data-spoken-buybox-config]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function stockState(variant, threshold) {
    if (!variant || !variant.available) return 'out';
    var tracked = !!variant.inventory_management;
    if (!tracked) return 'in';
    var qty = parseInt(variant.inventory_quantity, 10);
    if (isNaN(qty)) return 'in';
    if (qty <= 0) return 'backorder';
    // Strictly less than, matching the Liquid and Yuva's own product grid.
    if (qty < threshold) return 'low';
    return 'in';
  }

  // "ships within 24 hours" is a promise the workshop cannot keep over the
  // weekend, so it is dropped between Friday midday and Sunday midday New
  // Zealand time. Read in Pacific/Auckland explicitly rather than from the
  // visitor's clock: the promise is about when Spoken Cycles can dispatch, not
  // about where the shopper is. Pacific/Auckland also covers NZDT and NZST
  // without a hardcoded offset.
  //
  // Anything unexpected - the setting off, no Intl time zone support, an
  // unparseable hour - returns false, so the page understates rather than
  // promises a dispatch that will not happen.
  function dispatchAllowed(cfg) {
    if (!cfg.showDispatch || !cfg.dispatchSuffix) return false;

    var day, hour;
    try {
      new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Pacific/Auckland',
        weekday: 'short',
        hour: '2-digit',
        hourCycle: 'h23'
      })
        .formatToParts(new Date())
        .forEach(function (part) {
          if (part.type === 'weekday') day = part.value;
          if (part.type === 'hour') hour = parseInt(part.value, 10);
        });
    } catch (e) {
      return false;
    }

    if (!day || isNaN(hour)) return false;
    if (day === 'Sat') return false;
    if (day === 'Fri' && hour >= 12) return false;
    if (day === 'Sun' && hour < 12) return false;
    return true;
  }

  function applyMeta(root, variant, cfg) {
    // Without a variant there is nothing to decide from, so the server
    // rendered rows are left exactly as they are rather than guessed at.
    if (!cfg || !variant) return;

    var state = stockState(variant, cfg.threshold);
    var line = root.querySelector('[data-spoken-stock]');
    var text = root.querySelector('[data-spoken-stock-text]');

    if (line && text) {
      line.className =
        'spoken-stock' +
        (state === 'low' ? ' spoken-stock--low'
          : state === 'backorder' ? ' spoken-stock--pending'
          : state === 'out' ? ' spoken-stock--out' : '');
      var label =
        state === 'low'
          ? cfg.copy.lowPrefix + parseInt(variant.inventory_quantity, 10) + cfg.copy.lowSuffix
          : cfg.copy[state === 'backorder' ? 'backorder' : state === 'out' ? 'out' : 'in'];

      // Only the states that actually have stock on hand carry the promise.
      if ((state === 'in' || state === 'low') && dispatchAllowed(cfg)) {
        label += cfg.dispatchSuffix;
      }

      text.textContent = label;
    }

    var delivery = root.querySelector('[data-spoken-delivery]');
    if (delivery) delivery.hidden = state === 'out';

    // The delivery wording depends on the variant's price against the free
    // shipping minimum, so it is recomputed alongside everything else.
    var dTitle = root.querySelector('[data-spoken-delivery-title]');
    var note = root.querySelector('[data-spoken-delivery-note]');
    if (cfg.delivery && variant) {
      var free = parseInt(variant.price, 10) >= cfg.freeShippingMin;
      if (dTitle) dTitle.textContent = cfg.delivery[free ? 'freeTitle' : 'paidTitle'];
      if (note) note.textContent = cfg.delivery[free ? 'freeNote' : 'paidNote'];
    }
    if (note) note.hidden = state === 'backorder';

    if (state !== 'out') refreshPickup(root, variant, cfg);
  }

  // Pickup is per variant and per location, and none of it is in the variant
  // JSON, so this asks the same section endpoint Yuva's own pickup block uses.
  // The row is shown only for a variant that can actually be collected.
  function refreshPickup(root, variant, cfg) {
    var row = root.querySelector('[data-spoken-pickup]');
    if (!row || !variant) return;

    var place = row.querySelector('[data-spoken-pickup-place]');
    var note = row.querySelector('[data-spoken-pickup-note]');
    if (!place || !note) return;

    fetch('/variants/' + variant.id + '/?section_id=pickup-availability')
      .then(function (r) {
        return r.ok ? r.text() : '';
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var time = doc.querySelector('#pickUpAvailabilityPreview .pickup-availability-content p');
        var name = doc.querySelector('#pickUpAvailabilityPreview .pickup-availability-heading span');

        if (time && time.textContent.trim()) {
          note.textContent = time.textContent.trim();
          if (name) {
            var key = name.textContent.trim();
            place.textContent = (cfg.places && cfg.places[key]) || key;
          }
          row.hidden = false;
        } else {
          row.hidden = true;
        }
      })
      .catch(function () {
        /* leave the server-rendered row as it stands */
      });
  }

  function init(priceBox) {
    var productRoot = priceBox.closest('.yv-main-product-detail');
    if (!productRoot) return;

    var cfg = config(productRoot);
    var variants = variantData(productRoot);

    var run = function () {
      var variant = currentVariant(productRoot, variants, cfg);
      apply(priceBox, productRoot, variant);
      applyMeta(productRoot, variant, cfg);
    };

    run();

    // common.js replaces the whole price box innerHTML on variant change.
    new MutationObserver(run).observe(priceBox, {
      childList: true,
      subtree: true,
      characterData: true
    });

    // The hidden id input is what actually tracks the selected variant.
    productRoot.addEventListener('change', function () {
      window.setTimeout(run, 0);
    });
  }

  function start() {
    var boxes = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < boxes.length; i++) init(boxes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();


/*
  Spoken Cycles - long option lists become a dropdown.

  An option with more than ten values is unusable as a row of pills, so it is
  presented as a select instead.

  The theme's own option_style setting is global and its JS branches on a global
  variantStyle in two places - how it binds events, and how it marks values
  unavailable - so switching one option to the theme's dropdown markup while the
  rest stay swatches would break its availability handling.

  Yuva's radios are therefore left in the DOM and still drive everything. The
  select is a second control layered over them: choosing an option clicks the
  matching radio, which is exactly what the theme does itself in swatch mode,
  and the select is kept in step with whatever the theme does to those radios -
  including marking values sold out or hiding combinations that do not exist.
*/
(function () {
  'use strict';

  var MAX_PILLS = 10;

  function build(selector) {
    var list = selector.querySelector('ul.product-loop-variants');
    if (!list || selector.querySelector('.spoken-variant-select')) return;

    var radios = list.querySelectorAll('input.productOption');
    if (radios.length <= MAX_PILLS) return;

    var select = document.createElement('select');
    select.className = 'spoken-variant-select';

    var label = selector.querySelector('.yv-variant-title');
    if (label) select.setAttribute('aria-label', label.textContent.trim());

    Array.prototype.forEach.call(radios, function (radio) {
      var option = document.createElement('option');
      option.value = radio.value;
      option.textContent = radio.value;
      select.appendChild(option);
    });

    function syncFromRadios() {
      Array.prototype.forEach.call(radios, function (radio, i) {
        var option = select.options[i];
        if (!option) return;
        var item = radio.closest('li');
        option.disabled = radio.classList.contains('not-available');
        option.hidden = !!(item && item.classList.contains('hidden'));
        if (radio.checked) select.selectedIndex = i;
      });
    }

    select.addEventListener('change', function () {
      var radio = radios[select.selectedIndex];
      // click, not checked = true: the theme binds its handler to click here.
      if (radio && !radio.checked) radio.click();
    });

    // The theme rewrites these classes after its own handler has run, so the
    // select is refreshed from them rather than from the event itself.
    selector.addEventListener('change', function () {
      window.setTimeout(syncFromRadios, 0);
    });

    new MutationObserver(syncFromRadios).observe(list, {
      attributes: true,
      subtree: true,
      attributeFilter: ['class']
    });

    list.insertAdjacentElement('afterend', select);
    selector.classList.add('spoken-has-variant-select');
    syncFromRadios();
  }

  function start() {
    var selectors = document.querySelectorAll(
      '.yv-main-product-detail .product-option-selector'
    );
    Array.prototype.forEach.call(selectors, build);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
